'use client'

import React, { memo } from 'react'
import { Zap } from 'lucide-react'

// Types compatibles avec page.tsx
interface CabinetElementProps {
  element: any // WorkspaceElement
  cabinet: any // PowerCabinet
  isSelected: boolean
  isPreviewSelected: boolean
  isDragging: boolean
  isSelectedForConnection: boolean
  onClick: (element: any) => void
  showResizeHandle: boolean
  onResizeStart?: (e: React.MouseEvent, element: any) => void
}

const CabinetElement = memo(({
  element,
  cabinet,
  isSelected,
  isPreviewSelected,
  isDragging,
  isSelectedForConnection,
  onClick,
  showResizeHandle,
  onResizeStart
}: CabinetElementProps) => {
  return (
    <g data-onboarding="workspace-cabinet">
      {/* Indicateur de sélection pour connexion */}
      {isSelectedForConnection && (
        <>
          <rect
            x={element.position.x - 8}
            y={element.position.y - 8}
            width={element.size.width + 16}
            height={element.size.height + 16}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="3"
            rx="12"
            opacity="0.3"
          >
            <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite" />
          </rect>
          <rect
            x={element.position.x - 4}
            y={element.position.y - 4}
            width={element.size.width + 8}
            height={element.size.height + 8}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="2"
            rx="10"
          >
            <animate attributeName="stroke-width" values="2;3;2" dur="1.5s" repeatCount="indefinite" />
          </rect>
        </>
      )}
      
      {/* Armoire/Coffret électrique */}
      <rect
        x={element.position.x}
        y={element.position.y}
        width={element.size.width}
        height={element.size.height}
        fill="hsl(var(--led-processor))"
        stroke={
          isSelected ? "hsl(var(--primary))" :
          isPreviewSelected ? "hsl(var(--primary))" :
          "#DC2626"
        }
        strokeWidth={
          isSelected ? "3" :
          isPreviewSelected ? "2" :
          "2"
        }
        strokeDasharray={isPreviewSelected ? "4,2" : "none"}
        rx="8"
        className={`cursor-move ${isDragging ? 'opacity-80' : ''}`}
        onClick={() => onClick(element)}
      />
      
      {/* Icône et texte */}
      <foreignObject
        x={element.position.x + 8}
        y={element.position.y + 8}
        width={element.size.width - 16}
        height={element.size.height - 16}
      >
        <div className="text-xs text-white p-1 flex flex-col items-center justify-center h-full cursor-move" onClick={() => onClick(element)}>
          <Zap className="h-6 w-6 mb-1 text-red-500" />
          <span className="font-bold text-center">{cabinet.brand}</span>
          <span className="text-[10px] opacity-80 text-center">{cabinet.type}</span>
          <span className="text-[10px] font-bold text-red-400 mt-1">
            {(cabinet.maxPower / 1000).toFixed(1)} kW
          </span>
        </div>
      </foreignObject>
      
      {/* Handle de redimensionnement */}
      {showResizeHandle && onResizeStart && (
        <g
          data-resize-handle="true"
          className="cursor-nwse-resize"
          style={{ userSelect: 'none', pointerEvents: 'auto' } as any}
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onResizeStart(e, element)
          }}
        >
          <rect
            data-resize-handle="true"
            x={element.position.x + element.size.width - 7}
            y={element.position.y + element.size.height - 7}
            width="12"
            height="12"
            fill="hsl(var(--primary))"
            stroke="hsl(var(--background))"
            strokeWidth="1.5"
            rx="2"
          />
          <line
            x1={element.position.x + element.size.width - 3}
            y1={element.position.y + element.size.height + 2}
            x2={element.position.x + element.size.width + 2}
            y2={element.position.y + element.size.height - 3}
            stroke="hsl(var(--background))"
            strokeWidth="1"
          />
          <line
            x1={element.position.x + element.size.width - 3}
            y1={element.position.y + element.size.height - 1}
            x2={element.position.x + element.size.width - 1}
            y2={element.position.y + element.size.height - 3}
            stroke="hsl(var(--background))"
            strokeWidth="1"
          />
        </g>
      )}
    </g>
  )
}, (prevProps, nextProps) => {
  // Comparaison personnalisée pour éviter les re-renders inutiles
  return (
    prevProps.element.id === nextProps.element.id &&
    prevProps.element.position.x === nextProps.element.position.x &&
    prevProps.element.position.y === nextProps.element.position.y &&
    prevProps.element.size.width === nextProps.element.size.width &&
    prevProps.element.size.height === nextProps.element.size.height &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isPreviewSelected === nextProps.isPreviewSelected &&
    prevProps.isDragging === nextProps.isDragging &&
    prevProps.isSelectedForConnection === nextProps.isSelectedForConnection &&
    prevProps.showResizeHandle === nextProps.showResizeHandle
  )
})

CabinetElement.displayName = 'CabinetElement'

export default CabinetElement

